import { useMemo, useState, useEffect } from 'react';
import { ConditionalButtonMapping, NavigationButtons, WebNavigation } from '@terragotech/gen5-config-lib';
import { mapScenarios, NodeMapDefinition } from '@terragotech/gen5-datamapping-lib';
import { useDataMapping } from './useDataMapping';
import _ from 'lodash';
import { useConfig } from '@terragotech/gen5-shared-components';

// New state properties
export interface ActionButtonState {
  state: 'loading' | 'disabled' | 'hidden' | 'enabled';
  disabledMessage?: string;
  link?: string;
}

// Web navigation buttons with state
export type UseConditionalNavigation = ConditionalWebNavigation & ActionButtonState;

// new navigation buttons with state
export type ConditionalNavigationButtons = {
  order: string[];
  components: {
    [key: string]: UseConditionalNavigation;
  };
};

// web navigation button with submenu components with state
export type ConditionalWebNavigation = {
  type: 'navigation';
  label: string;
  icon: string;
  conditionalMap: NodeMapDefinition;
  subMenu: {
    order: string[];
    components: {
      [key: string]: UseConditionalNavigation;
    };
  };
};

// results of evaluating a button
export interface ConditionalButton {
  isDisabled: boolean;
  disabledMessage: string;
  isHidden: boolean;
  link: string;
}

export type EvaluatedNavigation = ConditionalButton & {
  type: 'navigation';
  label: string;
  icon: string;
  conditionalMap: NodeMapDefinition;
  subMenu: {
    order: string[];
    components: {
      [key: string]: EvaluatedNavigation;
    };
  };
};

// final output of function
export interface UseConditionalNavigations {
  state: 'loading' | 'ready';
  navigations?: ConditionalNavigationButtons;
}

// helper function to build web navigation button with state
const buildButtonWithState = (
  conditionalButton: UseConditionalNavigation,
  state: ActionButtonState['state'],
  disabledMessage?: string,
  link?: string
): UseConditionalNavigation => ({
  type: conditionalButton.type,
  label: conditionalButton.label,
  icon: conditionalButton.icon,
  conditionalMap: conditionalButton.conditionalMap,
  subMenu: conditionalButton.subMenu,
  state: state,
  disabledMessage: disabledMessage,
  link: link,
});

// helper function to map all components with state, including submenus
const buildInitialButtonState = (conditionalButton: WebNavigation): UseConditionalNavigation => {
  const subMenu = conditionalButton.subMenu;

  const updatedSubMenu = subMenu
    ? {
        ...subMenu,
        components: Object.entries(subMenu.components).reduce((acc, [key, value]) => {
          acc[key] = buildInitialButtonState(value);
          return acc;
        }, {} as { [key: string]: UseConditionalNavigation }),
      }
    : { components: {}, order: [] };

  // Build the button with state and include the updated subMenu if it exists
  return buildButtonWithState(
    {
      ...conditionalButton,
      subMenu: updatedSubMenu,
      state: 'loading',
    },
    'loading'
  );
};

// helper function to call initial state of navigations
const buildInitialNavigations = (navigations: NavigationButtons): ConditionalNavigationButtons => {
  const newComponents: { [key: string]: UseConditionalNavigation } = {};
  navigations?.order?.forEach(key => {
    newComponents[key] = buildInitialButtonState(navigations.components[key]);
  });
  return {
    order: navigations?.order || [],
    components: newComponents,
  };
};

const getStateFromMapResults = (result: ConditionalButtonMapping): ActionButtonState['state'] => {
  return result.isHidden ? 'hidden' : result.isDisabled ? 'disabled' : 'enabled';
};

const evaluateButtonState = async (
  conditionalButton: UseConditionalNavigation,
  accessors: any,
  functionDefinitions: any
): Promise<EvaluatedNavigation> => {
  if (conditionalButton.conditionalMap) {
    return mapScenarios.NAVIGATION_OPTIONS.evaluate(
      conditionalButton.conditionalMap,
      accessors,
      functionDefinitions
    ).then(results => {
      return { ...conditionalButton, ...results } as unknown as EvaluatedNavigation;
    });
  }

  return Promise.resolve({
    isDisabled: false,
    isHidden: false,
    disabledMessage: '',
    link: '',
    ...conditionalButton,
  } as unknown as EvaluatedNavigation);
};

// Recursive helper function to evaluate the button state and its submenus
const evaluateButtonStateRecursively = async (
  buttons: ConditionalNavigationButtons,
  accessors: any,
  functionDefinitions: any
): Promise<ConditionalNavigationButtons> => {
  const updatedComponents: { [key: string]: UseConditionalNavigation } = {};

  // Iterate through each button in the components
  await Promise.all(
    Object.entries(buttons.components).map(async ([key, button]) => {
      const buttonResult = await evaluateButtonState(button, accessors, functionDefinitions);

      const subMenu = button.subMenu;
      const updatedSubMenu = subMenu
        ? {
            ...subMenu,
            components: await evaluateButtonStateRecursively(
              { order: button.subMenu.order, components: button.subMenu.components },
              accessors,
              functionDefinitions
            ).then(res => res.components),
          }
        : { components: {}, order: [] };

      updatedComponents[key] = buildButtonWithState(
        {
          ...button,
          subMenu: updatedSubMenu,
        },
        getStateFromMapResults(buttonResult),
        buttonResult.disabledMessage,
        buttonResult.link
      );
    })
  );

  return {
    ...buttons,
    components: updatedComponents,
  };
};

const doesLoadingButtonExist = (conditionalButton: UseConditionalNavigation): boolean => {
  if (conditionalButton.state === 'loading') {
    return true;
  }

  const subMenu = conditionalButton.subMenu;
  if (subMenu) {
    return Object.values(subMenu.components).some(subButton => doesLoadingButtonExist(subButton));
  }

  return false;
};

const getVisibleButtons = (buttons: ConditionalNavigationButtons): ConditionalNavigationButtons => {
  const visibleComponents = Object.entries(buttons.components)
    .filter(([_, button]) => button.state !== 'hidden')
    .reduce((acc, [key, button]) => {
      const updatedButton = {
        ...button,
        isDisabled: button.state !== 'enabled',
      };

      if (button.subMenu) {
        updatedButton.subMenu = getVisibleButtons(button.subMenu);
      }

      acc[key] = updatedButton;
      return acc;
    }, {} as typeof buttons.components);

  const visibleOrder = buttons.order.filter(key => visibleComponents[key]);

  return {
    order: visibleOrder,
    components: visibleComponents,
  };
};

export const UseConditionalNavigations = (): UseConditionalNavigations => {
  const { navigations, functionDefinitions } = useConfig();

  const [buttons, setButtons] = useState<ConditionalNavigationButtons>(buildInitialNavigations(navigations));

  const [navs, setNavs] = useState<UseConditionalNavigations>({ state: 'loading' });

  const dataMapping = useDataMapping();
  const dataMappingContext = useMemo(() => {
    return dataMapping();
  }, [dataMapping]);

  useEffect(() => {
    const accessors = dataMapping().accessors;

    // Evaluate buttons and submenus recursively
    evaluateButtonStateRecursively(buttons, accessors, functionDefinitions).then(updatedButtons => {
      // Compare the new state with the current state to avoid unnecessary re-renders
      if (!_.isEqual(updatedButtons, buttons)) {
        setButtons(updatedButtons);
      }
    });
  }, [dataMappingContext, buttons, setButtons, dataMapping]);

  //Now whenever the state of our buttons changes, we need to reevaluate the state of all buttons on the card
  useEffect(() => {
    // If any button is still loading, then we are in a loading state
    const loadedButtons = Object.values(buttons.components).some(subButton => doesLoadingButtonExist(subButton));
    const cardState: UseConditionalNavigations['state'] = loadedButtons ? 'loading' : 'ready';
    //Now find the primary button
    const visibleButtons = getVisibleButtons(buttons);

    setNavs({ state: cardState, navigations: visibleButtons });
  }, [buttons]);

  return navs;
};
